/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { lazy, Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { type TriggerProps } from '@atlaskit/popup/types';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { PromptAgentAvatar } from '../../../common/ui/prompt-agent-avatar';

import messages from './messages';

const LazyAgentMenu = lazy(async () => ({
	default: (
		await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-agent-menu/agent-menu" */ '@atlassian/conversation-assistant-agent-menu'
		)
	).AgentMenu,
}));

const styles = cssMap({
	trigger: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		alignSelf: 'center',
		gap: token('space.050'),
		// Separates the trigger from the placeholder / prompt text.
		marginInlineEnd: token('space.075'),
		/*
		 * Optical alignment against the prompt's left edge. The trigger's own
		 * inline padding exists to give the hover background some breathing room,
		 * but it shouldn't compound the container's padding and push the icon
		 * inwards, so it is pulled back by the same amount.
		 */
		marginInlineStart: token('space.negative.050'),
		borderRadius: token('radius.large'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	/*
	 * `.ak-editor-content-area` carries its own internal padding, which sits
	 * inside the editor child only. That leaves the editor's text a little below
	 * the centre of the flex line, so a centred trigger looks high by comparison.
	 *
	 * Compact mode zeroes that padding (see `editorWrapperCompact` in
	 * `PromptView`), so the correction must not be applied there.
	 *
	 * Uses a relative offset rather than a margin: a margin on a centre-aligned
	 * flex item only shifts by half when the line has free space, so the nudge
	 * would vary with the prompt's height. This is exact and stays out of layout.
	 */
	triggerNudgedForEditorPadding: {
		position: 'relative',
		insetBlockStart: token('space.negative.050'),
	},

	icon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},

	/**
	 * Collapses the agent name + chevron to zero width (grid `1fr` -> `0fr`, which
	 * animates to the content's natural width without needing a fixed bound) so the
	 * resting state is icon-only. Mirrors the Post Stream Review controls animation
	 * in `editor-plugin-ai/.../PostStreamReview/AskRovo.tsx` — keep the two in sync.
	 * Motion is disabled under `prefers-reduced-motion`.
	 */
	expandingRegion: {
		display: 'grid',
		transitionProperty: 'grid-template-columns, opacity',
		transitionDuration: '0.2s',
		transitionTimingFunction: 'ease-out',
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0s',
		},
	},

	expanded: {
		gridTemplateColumns: '1fr',
		opacity: 1,
	},

	collapsed: {
		gridTemplateColumns: '0fr',
		opacity: 0,
	},

	// `overflow: hidden` + `min-width: 0` let the content be clipped as the grid
	// column collapses to `0fr`.
	expandingContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		overflow: 'hidden',
		minWidth: '0px',
		whiteSpace: 'nowrap',
		font: token('font.body'),
	},
});

export type AgentSelectorProps = {
	/**
	 * The currently selected agent, or `null`/`undefined` when the default Rovo
	 * assistant is handling the prompt.
	 */
	agent?: Agent | null;
	/** `null` clears the selection and returns the prompt to default Rovo. */
	onAgentSelect: (agent: Agent | null) => void;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	siteId: string;
	isDisabled?: boolean;
	/**
	 * Mirrors the prompt's `compact` mode, which strips the editor's internal
	 * padding and so removes the need to optically correct the trigger.
	 */
	compact?: boolean;
	/**
	 * Mirrors the prompt's own `shouldRenderInPortal`. The inline prompt dismisses
	 * on outside interaction, so unless the menu renders into the trigger's DOM
	 * tree, selecting an agent reads as an outside click and closes the prompt.
	 */
	shouldRenderInPortal?: boolean;
};

/**
 * Agent selector rendered to the LEFT of the inline prompt input.
 *
 * At rest it shows only the current agent's avatar (or the generic agent icon
 * for default Rovo). On hover / keyboard focus — and while the menu is open —
 * it expands to reveal the agent name and a chevron. Clicking opens the shared
 * `AgentMenu`, which supplies the "Switch to another agent" heading, agent
 * search, the agent list, and the Browse / Create agent actions.
 */
export const AgentSelector = ({
	agent,
	onAgentSelect,
	assistanceServiceParams,
	siteId,
	isDisabled,
	compact,
	shouldRenderInPortal,
}: AgentSelectorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isPointerOver, setIsPointerOver] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	// `AgentMenu` pulls in Relay and the agent permission controllers, so it is
	// only mounted once the user actually opens the menu. Until then the trigger
	// renders on its own and the prompt pays nothing for it.
	const [hasOpened, setHasOpened] = useState(false);

	// Keep the label expanded while the menu is open so the trigger doesn't
	// collapse out from under the popup when the pointer moves into it.
	const isExpanded = isOpen || isPointerOver || isFocused;

	const agentName = agent?.name ?? formatMessage(messages.defaultAgentName);

	/**
	 * Only keyboard focus should expand the trigger. Closing the menu returns
	 * focus here, so treating every focus as an expand left the label stuck open
	 * after picking an agent with the mouse.
	 */
	const handleFocus = useCallback((event: React.FocusEvent<HTMLButtonElement>) => {
		try {
			setIsFocused(event.currentTarget.matches(':focus-visible'));
		} catch {
			// `:focus-visible` is unsupported in some test environments.
			setIsFocused(false);
		}
	}, []);

	const handleAgentSelect = useCallback(
		(selectedAgent: Agent | undefined) => {
			// `AgentMenu` passes `undefined` for its "Chat with Rovo" item, which is
			// how the user gets back to the default assistant. Coerced to `null` so
			// the controller clears the selection rather than ignoring it.
			onAgentSelect(selectedAgent ?? null);
			setIsOpen(false);
		},
		[onAgentSelect],
	);

	const renderTrigger = useCallback(
		(triggerProps?: TriggerProps) => (
			<Pressable
				{...triggerProps}
				xcss={cx(styles.trigger, !compact && styles.triggerNudgedForEditorPadding)}
				testId="inline-chat-prompt-agent-selector"
				isDisabled={isDisabled}
				aria-label={formatMessage(messages.selectAgentLabel, { agentName })}
				aria-expanded={isOpen}
				aria-haspopup="menu"
				onClick={() => {
					setHasOpened(true);
					setIsOpen((open) => !open);
				}}
				onMouseEnter={() => setIsPointerOver(true)}
				onMouseLeave={() => setIsPointerOver(false)}
				onFocus={handleFocus}
				onBlur={() => setIsFocused(false)}
			>
				<span css={styles.icon}>
					{agent ? <PromptAgentAvatar agent={agent} /> : <RovoHexIcon label="" size="xsmall" />}
				</span>
				{/* Decorative: the trigger's `aria-label` already announces the current
				    agent, so this must not be read out a second time. */}
				<span
					css={[styles.expandingRegion, isExpanded ? styles.expanded : styles.collapsed]}
					aria-hidden="true"
					// The width animation is driven by Compiled atomic classes, which
					// jsdom can't resolve — this makes the state assertable in tests.
					data-expanded={isExpanded}
					data-testid="inline-chat-prompt-agent-selector-label"
				>
					<span css={styles.expandingContent}>
						{agentName}
						<ChevronDownIcon label="" />
					</span>
				</span>
			</Pressable>
		),
		[agent, agentName, compact, formatMessage, handleFocus, isDisabled, isExpanded, isOpen],
	);

	if (!hasOpened) {
		return renderTrigger();
	}

	return (
		// `AgentMenu` calls `useRelayEnvironment()`. The inline prompt is only
		// wrapped in a Relay provider on the agent-mentions path, so without this
		// the menu would throw for every other consumer. The conditional provider
		// reuses an ancestor environment when one already exists.
		<ConditionalRelayEnvironmentProvider>
			<Suspense fallback={renderTrigger()}>
				<LazyAgentMenu
					selectedAgent={agent ?? undefined}
					isAgentDropdownOpen={isOpen}
					setIsAgentDropdownOpen={setIsOpen}
					onClose={() => setIsOpen(false)}
					trigger={renderTrigger}
					placement="top-start"
					shouldRenderToParent={!shouldRenderInPortal}
					siteId={siteId}
					assistanceServiceParams={assistanceServiceParams}
					onAgentSelect={handleAgentSelect}
					touchpointSource="inline-chat-prompt-agent-selector"
					autofocus
					// `onAgentSelect` takes precedence over starting a new conversation,
					// which is Rovo Chat behaviour and not meaningful for the inline prompt.
					createConversationOptimistically={() => {}}
				/>
			</Suspense>
		</ConditionalRelayEnvironmentProvider>
	);
};
